import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Autocomplete,
  InputAdornment,
  Avatar,
  Divider,
  Chip,
  Paper,
  alpha,
  useTheme,
  styled
} from '@mui/material';
import {
  Send as SendIcon,
  Person as PersonIcon,
  AccountBalanceWallet as WalletIcon,
  CheckCircle as SuccessIcon,
  Schedule as PendingIcon,
  ContactMail as ContactIcon,
  ArrowForward as ArrowIcon,
  Cancel as CancelIcon,
  Payments as PaymentsIcon
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import memberService from '../../../services/memberService';

// Custom styled components
const StyledDialogTitle = styled(DialogTitle)(({ theme }) => ({
  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
  color: theme.palette.primary.contrastText,
  padding: theme.spacing(3),
  display: 'flex',
  alignItems: 'center',
}));

const CircleDecoration = styled('div')(({ theme, size = 100, top, right, left, bottom, opacity = 0.1 }) => ({
  position: 'absolute',
  width: size,
  height: size,
  borderRadius: '50%',
  background: alpha(theme.palette.common.white, opacity),
  top: top || 'auto',
  right: right || 'auto',
  left: left || 'auto',
  bottom: bottom || 'auto',
  zIndex: 0,
}));

const AmountTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    fontSize: '1.5rem',
    fontWeight: 'bold',
    '& fieldset': {
      borderRadius: theme.shape.borderRadius * 2,
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.primary.main,
      borderWidth: 2,
      boxShadow: `0 0 0 4px ${alpha(theme.palette.primary.main, 0.2)}`,
    },
  },
  '& .MuiInputLabel-root': {
    fontSize: '1.1rem',
    fontWeight: 500,
    '&.Mui-focused': {
      color: theme.palette.primary.main,
    },
  },
}));

const SummaryBox = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  borderRadius: theme.shape.borderRadius * 2,
  background: alpha(theme.palette.primary.main, 0.05),
  border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
  marginTop: theme.spacing(3),
  marginBottom: theme.spacing(3),
}));

const SummaryRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: theme.spacing(1, 0),
  '&:not(:last-child)': {
    borderBottom: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
  },
}));

const TransferTokensModal = ({ open, onClose, onTransfer, wallet }) => {
  const theme = useTheme();
  const [formData, setFormData] = useState({
    recipientMemberId: '',
    amount: '',
    note: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [recipients, setRecipients] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchLoading, setSearchLoading] = useState(false);
  const [transferComplete, setTransferComplete] = useState(false);
  const [transferStage, setTransferStage] = useState('form'); // form, pending, success
  const [selectedRecipient, setSelectedRecipient] = useState(null);

  useEffect(() => {
    if (open) {
      // Load initial member list when modal opens
      loadInitialMembers();
    }
  }, [open]);

  useEffect(() => {
    if (searchTerm.length > 2) {
      searchMembers(searchTerm);
    }
  }, [searchTerm]);

  const loadInitialMembers = async () => {
    setSearchLoading(true);
    try {
      const tenantId = localStorage.getItem('tenantId');
      if (!tenantId) return;

      const filters = { tenantId, status: 'active' };
      const response = await memberService.getAllMembers(1, 50, '', filters);

      let memberList = [];
      if (response && response.data && Array.isArray(response.data)) {
        memberList = response.data;
      } else if (response && Array.isArray(response)) {
        memberList = response;
      } else if (response && response.members && Array.isArray(response.members)) {
        memberList = response.members;
      } else {
        console.warn('Unexpected member response format', response);
        return;
      }

      const processedMembers = memberList.map((member) => {
        if (member.userId && typeof member.userId === 'object') {
          const firstName = member.userId.firstName || '';
          const lastName = member.userId.lastName || '';
          const fullName = `${firstName} ${lastName}`.trim();

          return {
            _id: member._id,
            name: fullName || 'Unnamed Member',
            email: member.userId.email || 'No email',
            status: member.status || 'unknown',
          };
        }

        const firstName = member.firstName || '';
        const lastName = member.lastName || '';
        const fullName = `${firstName} ${lastName}`.trim();

        return {
          _id: member._id,
          name: fullName || member.name || 'Unnamed Member',
          email: member.email || 'No email',
          status: member.status || 'unknown',
        };
      });

      setRecipients(processedMembers);
    } catch (error) {
      console.error('Error loading initial members:', error);
    } finally {
      setSearchLoading(false);
    }
  };

  const searchMembers = async (term) => {
    setSearchLoading(true);
    try {
      const response = await memberService.searchMembers(term);
      
      let memberList = [];
      if (response && response.data && Array.isArray(response.data)) {
        memberList = response.data;
      } else if (response && Array.isArray(response)) {
        memberList = response;
      } else if (response && response.members && Array.isArray(response.members)) {
        memberList = response.members;
      } else {
        console.warn('Unexpected member search response format', response);
        memberList = [];
      }

      const processedMembers = memberList.map((member) => {
        if (member.userId && typeof member.userId === 'object') {
          const firstName = member.userId.firstName || '';
          const lastName = member.userId.lastName || '';
          const fullName = `${firstName} ${lastName}`.trim();

          return {
            _id: member._id,
            name: fullName || 'Unnamed Member',
            email: member.userId.email || 'No email',
            status: member.status || 'unknown',
          };
        }

        const firstName = member.firstName || '';
        const lastName = member.lastName || '';
        const fullName = `${firstName} ${lastName}`.trim();

        return {
          _id: member._id,
          name: fullName || member.name || 'Unnamed Member',
          email: member.email || 'No email',
          status: member.status || 'unknown',
        };
      });

      setRecipients(processedMembers);
    } catch (err) {
      console.error('Error searching members:', err);
      setRecipients([]);
    } finally {
      setSearchLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const handleRecipientChange = (event, value) => {
    if (value) {
      setSelectedRecipient(value);
      setFormData({
        ...formData,
        recipientMemberId: value._id,
        recipientName: value.name || `${value.firstName} ${value.lastName}`.trim()
      });
    } else {
      setSelectedRecipient(null);
      setFormData({
        ...formData,
        recipientMemberId: '',
        recipientName: ''
      });
    }
  };

  const validateForm = () => {
    if (!formData.recipientMemberId) {
      setError('Please select a recipient');
      return false;
    }

    const amount = parseFloat(formData.amount);
    if (isNaN(amount) || amount <= 0) {
      setError('Please enter a valid amount greater than 0');
      return false;
    }

    if (amount > wallet.balance) {
      setError(`You don't have enough tokens. Your balance is ${wallet.balance} ${wallet.token?.symbol || 'tokens'}`);
      return false;
    }

    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setLoading(true);
    setError(null);
    setTransferStage('pending');

    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // Call the actual transfer function
      await onTransfer(formData);
      
      // Set success state
      setTransferComplete(true);
      setTransferStage('success');
      
      // Reset form after a delay
      setTimeout(() => {
        setFormData({
          recipientMemberId: '',
          amount: '',
          note: ''
        });
        setSelectedRecipient(null);
      }, 500);
    } catch (err) {
      setError(err.message || 'An error occurred during transfer');
      setTransferStage('form');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      recipientMemberId: '',
      amount: '',
      note: ''
    });
    setSelectedRecipient(null);
    setError(null);
    setTransferComplete(false);
    setTransferStage('form');
    onClose();
  };

  const getRecipientDisplay = () => {
    if (!selectedRecipient) return '';
    
    return selectedRecipient.name || 
      `${selectedRecipient.firstName || ''} ${selectedRecipient.lastName || ''}`.trim() || 
      selectedRecipient.email;
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose} 
      maxWidth="sm" 
      fullWidth
      PaperProps={{
        component: motion.div,
        initial: { opacity: 0, y: 20 },
        animate: { opacity: 1, y: 0 },
        transition: { duration: 0.3 },
        sx: { 
          borderRadius: theme.shape.borderRadius * 3,
          overflow: 'hidden',
          boxShadow: '0 10px 40px rgba(0, 0, 0, 0.15)'
        }
      }}
    >
      <StyledDialogTitle>
        <Avatar 
          sx={{ 
            bgcolor: 'rgba(255, 255, 255, 0.2)', 
            mr: 2, 
            p: 0.5,
            boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)'
          }}
        >
          <SendIcon />
        </Avatar>
        <Typography variant="h6" component="div" fontWeight="bold">
          Transfer Tokens
        </Typography>
      </StyledDialogTitle>
      
      <DialogContent sx={{ position: 'relative', p: 3 }}>
        <CircleDecoration size={200} top="-100px" right="-100px" opacity={0.03} />
        <CircleDecoration size={150} bottom="-50px" left="-50px" opacity={0.03} />
        
        {transferStage === 'success' ? (
          <Box 
            component={motion.div}
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}
            sx={{ 
              textAlign: 'center', 
              py: 4, 
              position: 'relative', 
              zIndex: 1,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center'
            }}
          >
            <Avatar
              sx={{
                width: 80,
                height: 80,
                mb: 2,
                bgcolor: alpha(theme.palette.success.main, 0.1),
                color: theme.palette.success.main,
                boxShadow: `0 0 0 8px ${alpha(theme.palette.success.main, 0.1)}`
              }}
            >
              <SuccessIcon sx={{ fontSize: 40 }} />
            </Avatar>
            
            <Typography variant="h5" gutterBottom fontWeight="bold">
              Transfer Successful!
            </Typography>
            
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3, maxWidth: 400 }}>
              You have successfully transferred {formData.amount} {wallet.token?.symbol || 'tokens'} to {getRecipientDisplay()}.
            </Typography>
            
            <Paper 
              elevation={0} 
              sx={{ 
                p: 3, 
                width: '100%', 
                bgcolor: alpha(theme.palette.success.main, 0.05),
                border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`,
                borderRadius: 2
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="subtitle2" color="text.secondary">Amount:</Typography>
                <Typography variant="subtitle1" fontWeight="bold">{formData.amount} {wallet.token?.symbol || 'tokens'}</Typography>
              </Box>
              
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="subtitle2" color="text.secondary">Recipient:</Typography>
                <Typography variant="subtitle1" fontWeight="bold">{getRecipientDisplay()}</Typography>
              </Box>
              
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="subtitle2" color="text.secondary">Status:</Typography>
                <Chip 
                  label="Completed" 
                  color="success" 
                  size="small" 
                  icon={<SuccessIcon />} 
                  sx={{ fontWeight: 'bold' }}
                />
              </Box>
            </Paper>
          </Box>
        ) : transferStage === 'pending' ? (
          <Box 
            component={motion.div}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.3 }}
            sx={{ 
              textAlign: 'center', 
              py: 4, 
              position: 'relative', 
              zIndex: 1,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center'
            }}
          >
            <Avatar
              sx={{
                width: 80,
                height: 80,
                mb: 2,
                bgcolor: alpha(theme.palette.primary.main, 0.1),
                color: theme.palette.primary.main
              }}
            >
              <CircularProgress size={40} />
            </Avatar>
            
            <Typography variant="h5" gutterBottom fontWeight="bold">
              Processing Transfer
            </Typography>
            
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3, maxWidth: 400 }}>
              Please wait while we process your transfer of {formData.amount} {wallet.token?.symbol || 'tokens'} to {getRecipientDisplay()}.
            </Typography>
            
            <Box sx={{ width: '100%', position: 'relative' }}>
              <Box 
                component={motion.div} 
                initial={{ width: 0 }}
                animate={{ width: '100%' }}
                transition={{ duration: 2 }}
                sx={{ 
                  height: 6,
                  background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                  borderRadius: 3
                }}
              />
            </Box>
          </Box>
        ) : (
          <Box 
            component={motion.div}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.3 }}
            sx={{ position: 'relative', zIndex: 1 }}
          >
            {error && (
              <Alert 
                severity="error" 
                sx={{ 
                  mb: 3, 
                  borderRadius: theme.shape.borderRadius * 2,
                  boxShadow: '0 4px 12px rgba(211, 47, 47, 0.2)'
                }}
              >
                {error}
              </Alert>
            )}
            
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              Select Recipient
            </Typography>
            
            <Autocomplete
              options={recipients}
              getOptionLabel={(option) => 
                option.name || `${option.firstName || ''} ${option.lastName || ''}`.trim() || option.email
              }
              onChange={handleRecipientChange}
              onInputChange={(event, value) => setSearchTerm(value)}
              loading={searchLoading}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Recipient"
                  variant="outlined"
                  fullWidth
                  margin="normal"
                  required
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <InputAdornment position="start">
                        <ContactIcon color="primary" />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <>
                        {searchLoading ? <CircularProgress color="inherit" size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                    sx: { borderRadius: theme.shape.borderRadius * 2 }
                  }}
                />
              )}
              renderOption={(props, option) => (
                <li {...props}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Avatar sx={{ width: 36, height: 36, mr: 1.5, bgcolor: alpha(theme.palette.primary.main, 0.1) }}>
                      <PersonIcon color="primary" />
                    </Avatar>
                    <Box>
                      <Typography variant="body1">
                        {option.name || `${option.firstName || ''} ${option.lastName || ''}`.trim() || option.email}
                      </Typography>
                      {option.email && (
                        <Typography variant="caption" color="text.secondary">
                          {option.email}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </li>
              )}
            />
            
            <Box sx={{ mt: 3 }}>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                Transfer Amount
              </Typography>
              
              <AmountTextField
                margin="normal"
                required
                fullWidth
                label="Amount"
                name="amount"
                type="number"
                value={formData.amount}
                onChange={handleChange}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <Typography 
                        variant="h6" 
                        color="primary" 
                        fontWeight="bold"
                      >
                        {wallet?.token?.symbol || 'tokens'}
                      </Typography>
                    </InputAdornment>
                  ),
                  inputProps: { min: 0, step: 'any' }
                }}
                placeholder="0.00"
              />
              
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                mt: 1, 
                p: 2, 
                bgcolor: alpha(theme.palette.background.paper, 0.7),
                borderRadius: theme.shape.borderRadius * 2
              }}>
                <Chip 
                  icon={<WalletIcon />} 
                  label={`Available: ${wallet?.balance || 0} ${wallet?.token?.symbol || 'tokens'}`} 
                  variant="outlined" 
                  color="primary"
                  sx={{ fontWeight: 'medium' }}
                />
                
                {wallet?.settings?.maxDailyTransferLimit > 0 && (
                  <Chip 
                    icon={<PaymentsIcon />} 
                    label={`Daily limit: ${wallet.settings.maxDailyTransferLimit} ${wallet?.token?.symbol || 'tokens'}`} 
                    variant="outlined"
                    sx={{ fontWeight: 'medium' }}
                  />
                )}
              </Box>
            </Box>
            
            <TextField
              margin="normal"
              fullWidth
              label="Note (Optional)"
              name="note"
              value={formData.note}
              onChange={handleChange}
              multiline
              rows={2}
              placeholder="Add a message for the recipient"
              InputProps={{
                sx: { borderRadius: theme.shape.borderRadius * 2 }
              }}
            />
            
            {formData.amount && !isNaN(parseFloat(formData.amount)) && parseFloat(formData.amount) > 0 && selectedRecipient && (
              <SummaryBox 
                component={motion.div}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
              >
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                  Transfer Summary
                </Typography>
                
                <SummaryRow>
                  <Typography variant="body2" color="text.secondary">
                    From
                  </Typography>
                  <Typography variant="body2" fontWeight="medium">
                    You
                  </Typography>
                </SummaryRow>
                
                <SummaryRow>
                  <Typography variant="body2" color="text.secondary">
                    To
                  </Typography>
                  <Typography variant="body2" fontWeight="medium">
                    {getRecipientDisplay()}
                  </Typography>
                </SummaryRow>
                
                <SummaryRow>
                  <Typography variant="body2" color="text.secondary">
                    Amount
                  </Typography>
                  <Typography variant="body2" fontWeight="bold" color="primary.main">
                    {formData.amount} {wallet?.token?.symbol || 'tokens'}
                  </Typography>
                </SummaryRow>
                
                <SummaryRow>
                  <Typography variant="body2" color="text.secondary">
                    Remaining Balance
                  </Typography>
                  <Typography 
                    variant="body2" 
                    fontWeight="bold"
                    color={wallet.balance - parseFloat(formData.amount) < 0 ? 'error.main' : 'success.main'}
                  >
                    {(wallet.balance - parseFloat(formData.amount)).toFixed(2)} {wallet?.token?.symbol || 'tokens'}
                  </Typography>
                </SummaryRow>
              </SummaryBox>
            )}
          </Box>
        )}
      </DialogContent>
      
      <DialogActions sx={{ p: 3, position: 'relative', zIndex: 1 }}>
        {transferStage === 'success' ? (
          <Button 
            onClick={handleClose} 
            variant="contained"
            color="primary"
            fullWidth
            sx={{ 
              py: 1, 
              borderRadius: theme.shape.borderRadius * 2,
              fontWeight: 'bold'
            }}
          >
            Done
          </Button>
        ) : transferStage === 'pending' ? (
          <Button 
            onClick={handleClose} 
            variant="outlined"
            color="primary"
            disabled
            startIcon={<CancelIcon />}
            sx={{ 
              py: 1, 
              borderRadius: theme.shape.borderRadius * 2,
              fontWeight: 'medium'
            }}
          >
            Cancel
          </Button>
        ) : (
          <>
            <Button 
              onClick={handleClose} 
              color="inherit"
              sx={{ 
                borderRadius: theme.shape.borderRadius * 2,
                fontWeight: 'medium'
              }}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleSubmit} 
              variant="contained"
              color="primary"
              disabled={loading || !formData.amount || !formData.recipientMemberId}
              startIcon={<SendIcon />}
              sx={{ 
                py: 1, 
                px: 3,
                borderRadius: theme.shape.borderRadius * 2,
                fontWeight: 'bold'
              }}
            >
              Transfer Tokens
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default TransferTokensModal;